import React, { useEffect, useRef, useState } from 'react';
import { useLang } from '../../i18n/LanguageContext';

const STAGES = {
  en: [['💍', 'Marriage', 'A new family begins. A term plan makes sure your partner is never left alone with loans or bills.', 'Term life cover'], ['👶', 'First child', 'A family health floater covers everyone, and a small SIP starts the education fund.', 'Health floater + SIP'], ['🏡', 'New home', 'Home insurance guards your biggest purchase against fire, theft and floods.', 'Home insurance'], ['🎓', "Child's college", 'The SIP you started years ago is ready when the admission letter arrives.', 'Goal planning'], ['🌅', 'Retirement', 'Your savings now pay a monthly income, so the later years stay worry-free.', 'Retirement plan']],
  bn: [['💍', 'বিয়ে', 'নতুন সংসারের শুরু। টার্ম প্ল্যান নিশ্চিত করে, ঋণ বা খরচের বোঝা জীবনসঙ্গীকে একা বইতে হবে না।', 'টার্ম লাইফ কভার'], ['👶', 'প্রথম সন্তান', 'ফ্যামিলি ফ্লোটারে সবাই সুরক্ষিত, আর ছোট একটি SIP দিয়ে শুরু পড়াশোনার তহবিল।', 'হেলথ ফ্লোটার + SIP'], ['🏡', 'নতুন বাড়ি', 'গৃহ বিমা জীবনের সবচেয়ে বড় কেনাকাটাকে আগুন, চুরি ও বন্যা থেকে রক্ষা করে।', 'গৃহ বিমা'], ['🎓', 'সন্তানের কলেজ', 'বহু বছর আগে শুরু করা SIP তৈরি থাকে, যখন ভর্তির চিঠি আসে।', 'লক্ষ্যভিত্তিক পরিকল্পনা'], ['🌅', 'অবসর', 'সঞ্চয় এখন প্রতি মাসে আয় দেয়, যাতে পরের দিনগুলো নিশ্চিন্তে কাটে।', 'অবসর পরিকল্পনা']],
};
// Where each badge sits around the lamp (right-side badges anchor to the right edge so they never overflow).
const CHIP_POS = [{ left: '4%', top: '10%' }, { right: '2%', top: '4%' }, { right: '2%', top: '56%' }, { left: '2%', top: '62%' }, { left: '50%', top: '80%', transform: 'translateX(-50%)' }];

// The one dark band: a sticky scroll story where the lamp brightens as each life stage gets covered.
export default function LampJourney() {
  const { lang, t } = useLang();
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const el = ref.current; if (!el) return;
      const total = el.offsetHeight - window.innerHeight;
      const p = total > 0 ? Math.min(1, Math.max(0, -el.getBoundingClientRect().top / total)) : 1;
      setProgress(p);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, []);

  const idx = Math.min(4, Math.floor(progress * 5 + 1e-4));
  const reached = progress > 0.02 ? idx + 1 : 0;
  const level = reached / 5;
  const stages = STAGES[lang];

  return (
    <section ref={ref} className="relative bg-night text-white" style={{ height: '460vh' }} aria-label={t('jTitle')}>
      <div className="sticky top-16 h-[calc(100vh-4rem)] overflow-hidden">
        <div className="max-w-7xl mx-auto h-full px-4 sm:px-6 grid lg:grid-cols-2 gap-4 lg:gap-12 items-center py-4 sm:py-6">
          <div className="order-2 lg:order-1">
            <p className="kicker text-amber-300 hidden sm:block">{t('jKick')}</p>
            <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight">{t('jTitle')}</h2>
            <ol className="mt-5 sm:mt-6 space-y-3 sm:space-y-4">
              {stages.map(([ic, title, text], i) => (
                <li key={title} className={`flex gap-3 sm:gap-4 transition-opacity duration-300 ${i === idx ? 'opacity-100' : 'opacity-40'}`}>
                  <div className="w-9 h-9 sm:w-11 sm:h-11 shrink-0 rounded-xl bg-white/10 flex items-center justify-center text-lg" aria-hidden="true">{ic}</div>
                  <div><div className="font-bold sm:text-lg">{title}</div>{i === idx && <p className="text-sm text-white/70">{text}</p>}</div>
                </li>
              ))}
            </ol>
          </div>
          <div className="order-1 lg:order-2 relative h-[34vh] sm:h-[50vh] lg:h-[62vh] flex items-center justify-center">
            <div className="absolute w-[60vmin] h-[60vmin] rounded-full transition-all duration-500"
              style={{ background: 'radial-gradient(circle, rgba(255,170,80,.55), rgba(242,106,33,.12) 45%, transparent 70%)', transform: `scale(${0.35 + level * 0.9})`, opacity: 0.35 + level * 0.65 }} />
            <svg viewBox="0 0 200 200" className="relative w-[28vmin] h-[28vmin] max-w-[220px] max-h-[220px]" aria-hidden="true">
              <defs>
                <linearGradient id="kc-fl" x1="0" y1="1" x2="0" y2="0"><stop offset="0" stopColor="#F26A21" /><stop offset=".6" stopColor="#FDBA4D" /><stop offset="1" stopColor="#FFF4C2" /></linearGradient>
                <linearGradient id="kc-bowl" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#E8A23A" /><stop offset="1" stopColor="#9A4A12" /></linearGradient>
              </defs>
              <g style={{ transformOrigin: '100px 100px', transform: `scale(${0.75 + level * 0.45})`, transition: 'transform .4s' }}>
                <path className="flame" d="M100 30 C112 55 124 70 118 92 C114 106 86 106 82 92 C76 70 90 58 100 30 Z" fill="url(#kc-fl)" />
              </g>
              <path d="M40 118 Q100 104 160 118 Q150 160 100 162 Q50 160 40 118 Z" fill="url(#kc-bowl)" />
              <ellipse cx="100" cy="118" rx="60" ry="9" fill="#F5C26B" />
              <path d="M70 175 Q100 166 130 175 L124 186 Q100 180 76 186 Z" fill="#9A4A12" />
            </svg>
            {stages.map(([ic, , , chip], i) => (
              <div key={chip} style={CHIP_POS[i]}
                className={`absolute text-[11px] sm:text-xs font-semibold bg-white text-ink rounded-full shadow-lg px-2.5 py-1.5 whitespace-nowrap transition-all duration-500 ${i < reached ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>{ic} {chip} ✓</div>
            ))}
            <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-64 max-w-[80%]">
              <div className="flex justify-between text-xs text-white/70 mb-1"><span>{t('jMeter')}</span><span>{Math.round(level * 100)}%</span></div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-saffron to-amber-300 transition-all duration-500" style={{ width: `${level * 100}%` }} /></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
